import type {
  OperationsNode,
  OperationsTeamNode,
} from '../types/operations.types'
import { isTeamNode } from './nodeTypes'
import { findNodeById } from './findNodeById'
import { findNodeParent } from './findNodeParent'
import { canDropNode } from './canDropNode'
import {
  resolveSeedPlacement,
  type SeedPlacement,
} from './resolveSeedPlacement'
import { resolveLandedOrder } from './resolveLandedOrder'

export type DropIntent =
  | { kind: 'none' }
  | {
      kind: 'move-team'
      teamId: string
      fromScheduleId: string
      toScheduleId: string
      placement: SeedPlacement
    }

const NONE: DropIntent = { kind: 'none' }

/**
 * Translates a dnd-kit drop (two node ids) into the single API call it means.
 *
 * Only TEAM drops produce an intent. Divisions and conferences are ordered
 * through the inline Order cell, not by dragging.
 *
 * Kept out of the components so the rail and the grid cannot derive different
 * intents from the same gesture. Returns `none` for anything illegal — the
 * caller does not need its own guards.
 */
export const resolveTreeDrop = (
  nodes: OperationsNode[],
  activeId: string,
  overId: string,
  /** Land BELOW `overId` rather than above it — the pointer's slot. */
  after = false
): DropIntent => {
  if (activeId === overId) {
    return NONE
  }

  const dragged = findNodeById(nodes, activeId)
  const over = findNodeById(nodes, overId)
  if (!dragged || !over) {
    return NONE
  }

  const draggedParent = findNodeParent(nodes, activeId)

  // Dropping ONTO a division targets that division's team list; dropping onto a
  // team targets the list that team already sits in.
  const targetParent = isTeamNode(over)
    ? findNodeParent(nodes, overId)
    : over.type === 'division'
    ? over
    : null

  // Dropping on the header of the division the team ALREADY belongs to is not a
  // move — it is the overshoot you get when aiming at the last row of the
  // division above, whose header sits directly between the two. Treating it as
  // a move sent a self-move the server accepted as a no-op, and the row snapped
  // back to its original position the moment the refetch landed.
  if (!isTeamNode(over) && draggedParent && over.id === draggedParent.id) {
    return NONE
  }

  if (!canDropNode({ dragged, draggedParent, targetParent })) {
    return NONE
  }
  // canDropNode has already rejected null parents and non-team drags.
  const from = draggedParent as NonNullable<typeof draggedParent>
  const to = targetParent as NonNullable<typeof targetParent>

  const destination = to.children.filter(isTeamNode) as OperationsTeamNode[]

  // The siblings AFTER the drop — the same thing legacy read off
  // `nextParentNode.children` before deriving `seed_action` from it. One rule
  // covers reorder-within and move-across, so the two cannot drift apart.
  const landedIds = resolveLandedOrder(
    destination.map((node) => node.id),
    activeId,
    overId,
    after
  )

  // A drop that leaves the team exactly where it already was is not a move.
  // The `activeId === overId` check above cannot catch these: the drop is
  // measured against a NEIGHBOUR, which is a different row, and only the
  // resulting order reveals that nothing changed — dropping below the sibling
  // above you, or above the sibling below you, both land you back in place.
  //
  // Same parent only. Landing at the same INDEX in a different division is a
  // genuine move and must still be sent.
  if (from.id === to.id) {
    const before = destination.map((node) => node.id)
    const unchanged =
      before.length === landedIds.length &&
      before.every((id, index) => id === landedIds[index])
    if (unchanged) {
      return NONE
    }
  }

  const byId = new Map<string, OperationsTeamNode>([
    ...destination.map((node): [string, OperationsTeamNode] => [node.id, node]),
    [activeId, dragged as OperationsTeamNode],
  ])
  const landed = landedIds
    .map((id) => byId.get(id))
    .filter((node): node is OperationsTeamNode => Boolean(node))

  return {
    kind: 'move-team',
    teamId: (dragged as OperationsTeamNode).teamId,
    fromScheduleId: from.id,
    toScheduleId: to.id,
    placement: resolveSeedPlacement(landed, activeId),
  }
}
